import Component from '@glimmer/component'
import { action } from '@ember/object'
import { isAfter } from 'date-fns'
import { fn } from '@ember/helper'
import { t } from 'ember-intl'
import { includes } from 'teacher-ui-client/helpers/includes'
import { BookLibraryCollectionInlineEdit } from 'teacher-ui-client/components/book-library/collection-inline-edit'
import { CollectionSelectOption } from 'teacher-ui-client/components/book-library/collection-select/option'
import type BookLibraryCollection from 'teacher-ui-client/models/book-library/collection'

interface Args {
  collections?: BookLibraryCollection[]
  collectionsWithBook?: BookLibraryCollection[]
  onCreate: (collection: BookLibraryCollection) => void
  onSelect: (collection: BookLibraryCollection) => void
  closeAction: () => void
  title: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class BookLibraryCollectionSelect extends Component<Signature> {
  get collections() {
    const { collections = [] } = this.args
    return collections.filter((collection) => !collection.isNew) // don't show any collections that are being created
  }

  get collectionsWithBook() {
    return this.args.collectionsWithBook || []
  }

  get splitCollections() {
    const { collections } = this

    const regularCollections = collections.filter((collection) => !collection.isFavourite)

    const mostRecent = regularCollections.reduce(
      (mostRecentCollection: BookLibraryCollection | null, collection: BookLibraryCollection) => {
        if (mostRecentCollection) {
          return isAfter(mostRecentCollection.lastUpdated, collection.lastUpdated) ? mostRecentCollection : collection
        } else {
          return collection
        }
      },
      null,
    )
    const sorted = regularCollections.sort((a, b) => {
      if (a.name === b.name) return 0
      return a.name < b.name ? -1 : 1
    })

    return { mostRecent, sorted }
  }

  get mostRecentCollection() {
    return this.splitCollections.mostRecent
  }

  get sortedCollections() {
    return this.splitCollections.sorted
  }

  @action
  onCreate(collection: BookLibraryCollection) {
    this.args.onCreate(collection)
    this.args.closeAction()
  }

  @action
  onSelect(collection: BookLibraryCollection) {
    this.args.onSelect(collection)
    this.args.closeAction()
  }

  <template>
    <div data-test-dropdown-content class="border-dusty-black-100 rounded-md border bg-white shadow-xl" ...attributes>
      <h4 class="m-3">{{@title}}</h4>
      <hr class="border-dusty-black-100 mx-3 my-0" />
      <ul class="p-2">
        <li data-test-create-collection>
          <div class="min-w-80 p-1">
            <BookLibraryCollectionInlineEdit
              @prefixIcon="phosphor-plus-bold"
              @buttonIcon="phosphor-folder-plus-fill"
              @onSuccess={{this.onCreate}}
              @onFail={{this.onCreate}}
            >
              <span data-test-create-collection-text>
                {{t "bookLibrary.components.collectionSelect.createLabel"}}
              </span>
            </BookLibraryCollectionInlineEdit>
          </div>
        </li>
      </ul>
      {{#if this.mostRecentCollection}}
        <hr class="border-dusty-black-100 mx-3 my-0" />
        <ul class="p-2">
          <li data-test-most-recent-label>
            <div class="text-dusty-black-200 p-1 italic">
              {{t "bookLibrary.components.collectionSelect.addToRecent"}}
            </div>
          </li>
          {{#let (includes this.collectionsWithBook this.mostRecentCollection) as |bookInCollection|}}
            <li data-test-most-recent-collection>
              <CollectionSelectOption
                class="p-1"
                @collectionName={{this.mostRecentCollection.name}}
                @collectionBookCount={{this.mostRecentCollection.count}}
                @bookInCollection={{bookInCollection}}
                @onSelect={{fn this.onSelect this.mostRecentCollection}}
              />
            </li>
          {{/let}}
        </ul>
      {{/if}}
      <hr class="border-dusty-black-100 mx-3 my-0" />
      <ul class="max-h-80 overflow-y-auto p-2">
        <li data-test-existing-label>
          <div class="text-dusty-black-200 p-1 italic">
            {{t "bookLibrary.components.collectionSelect.addToExisting"}}
          </div>
        </li>
        {{#each this.sortedCollections as |collection index|}}
          {{#let (includes this.collectionsWithBook collection) as |bookInCollection|}}
            <li data-test-existing-collection={{index}}>
              <CollectionSelectOption
                class="p-1"
                @collectionName={{collection.name}}
                @collectionBookCount={{collection.count}}
                @bookInCollection={{bookInCollection}}
                @onSelect={{fn this.onSelect collection}}
              />
            </li>
          {{/let}}
        {{else}}
          <li>
            <div class="p-1">{{t "bookLibrary.components.collectionSelect.empty"}}</div>
          </li>
        {{/each}}
      </ul>
    </div>
  </template>
}

export default BookLibraryCollectionSelect
